import { useMemo, memo } from 'react';

import { useCheckoutContext, Article, Price, PriceType } from '@zooplus/checkout-state';
import { ProductPrice } from '@zooplus/ui-components';

import { CHECKOUT_STATE_PROJECT_ID, PAGE_TYPE, ARTICLE_PRICE_TYPE_V2 } from '@utilities/constants';

type ItemPriceCartV2Props = {
  article: Article;
  commonStyle?: string;
};

const getPrice = (article: Article): string => (article?.price as Price)?.label ?? '';

const getPricePrefix = (article: Article): string =>
  article.referencePrice
    ? `${article.referencePrice?.labelPrefix} ${article.referencePrice?.label}`
    : '';

const getRefPrice = (article: Article, isOmnibusDirectiveFeatureEnabled: boolean) => {
  if (article?.referencePrice) {
    return {
      amount: article.referencePrice?.label ?? '',
      label: article.referencePrice?.labelPrefix ?? '',
      difference:
        isOmnibusDirectiveFeatureEnabled && article?.tooltip?.discountLabel
          ? article.tooltip.discountLabel
          : '',
    };
  }
  return undefined;
};

const getTooltipProps = (article: Article, isOmnibusDirectiveFeatureEnabled: boolean) => {
  if (isOmnibusDirectiveFeatureEnabled && article.tooltip) {
    return {
      id: 'product-reduced-price-tooltip',
      label: 'more info',
      text: article.tooltip?.text,
      width: 'md' as const,
      position: 'right-middle' as const,
      breakpointPlacements: {
        lg: { position: 'top-middle' as const },
      },
    };
  }
  return undefined;
};

const getTextAlign = (isPreviewPage: boolean) => ({
  all: 'left' as 'right' | 'left',
  md: (isPreviewPage ? 'right' : 'left') as 'right' | 'left',
});

const usePriceProps = (
  article: Article,
  isPreviewPage: boolean,
  isOmnibusDirectiveFeatureEnabled: boolean,
) => {
  return useMemo(
    () => ({
      tooltipProps: getTooltipProps(article, isOmnibusDirectiveFeatureEnabled),
      price: getPrice(article),
      refPrice: getRefPrice(article, isOmnibusDirectiveFeatureEnabled),
      pricePrefix: getPricePrefix(article),
      textAlign: getTextAlign(isPreviewPage),
    }),
    [article, isPreviewPage, isOmnibusDirectiveFeatureEnabled],
  );
};

const ItemPriceCartV2 = ({ article, commonStyle }: ItemPriceCartV2Props) => {
  const { mainTogglesMap = {}, pageType } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const isCartPage = pageType === PAGE_TYPE.WEB_CART;
  const isPreviewPage = pageType === PAGE_TYPE.PREVIEW;
  const isOmnibusDirectiveFeatureEnabled = mainTogglesMap.CHECKOUT_OMNIBUS_DIRECTIVE_FEATURE;
  const isReducedPrice = article.priceType !== ARTICLE_PRICE_TYPE_V2.REGULAR;
  const priceProps = usePriceProps(article, isPreviewPage, isOmnibusDirectiveFeatureEnabled);

  return (
    <ProductPrice
      data-testid="article-price"
      isReducedPrice={isReducedPrice}
      className={commonStyle}
      isPriceBold={isCartPage}
      textAlign={priceProps.textAlign}
      price={priceProps.price}
      priceType={(article.priceType as PriceType) ?? ''}
      pricePrefix={priceProps.pricePrefix}
      refPrice={priceProps.refPrice}
      tooltip={priceProps.tooltipProps}
    />
  );
};

export default memo(ItemPriceCartV2);
